import { useSyncExternalStore } from 'react';

/**
 * The reader's theme and language, kept in localStorage (`vlm.theme`, `vlm.lang` — each app has
 * its own origin, so each its own choice). The app's index.html applies both before the first
 * paint; this store keeps them from then on. No state library: one tiny external store.
 */

export type Theme = 'day' | 'night' | 'auto';
export type PrefLang = 'en' | 'ar';

const THEME_KEY = 'vlm.theme';
const LANG_KEY = 'vlm.lang';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked: the choice lasts for this page
  }
}

interface PrefsState {
  theme: Theme;
  lang: PrefLang;
}

let state: PrefsState = {
  theme: ((t) => (t === 'night' || t === 'auto' ? t : 'day'))(read(THEME_KEY)),
  lang: read(LANG_KEY) === 'ar' ? 'ar' : 'en',
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const prefs = {
  get: (): PrefsState => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  setTheme(theme: Theme) {
    write(THEME_KEY, theme);
    document.documentElement.setAttribute('data-theme', theme);
    state = { ...state, theme };
    emit();
  },
  /** Local only; the caller saves the signed-in user's own language when it should. */
  setLang(lang: PrefLang) {
    write(LANG_KEY, lang);
    state = { ...state, lang };
    emit();
  },
};

/** `const theme = usePrefs((p) => p.theme)` — re-renders when the selected value changes. */
export function usePrefs<T>(select: (p: PrefsState) => T): T {
  return useSyncExternalStore(prefs.subscribe, () => select(state), () => select(state));
}

const DARK = '(prefers-color-scheme: dark)';

function subscribeDark(onChange: () => void) {
  const media = window.matchMedia(DARK);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

/** The theme actually on screen — "auto" resolved against the OS. Maps and charts read colours with it. */
export function useScheme(): 'day' | 'night' {
  const theme = usePrefs((p) => p.theme);
  const dark = useSyncExternalStore(subscribeDark, () => window.matchMedia(DARK).matches, () => false);
  return theme === 'auto' ? (dark ? 'night' : 'day') : theme;
}
